import maplibregl, {MapLibreZoomEvent, Marker} from "maplibre-gl";
import {Position} from "geojson";
import {MapSourceDataEvent} from "mapbox-gl";
import {boundingBoxFrom} from "../bounding-box";
import {MapPoint} from "../MapPoint";
import {isMapboxURL, transformMapboxUrl} from "maplibregl-mapbox-request-transformer";
import {MapConfig} from "../MapConfig";

export class MaplibreClient {
    private map: maplibregl.Map | undefined;
    private mapConfig: MapConfig;

    private createListing: (priceTag: string, id: string, exactLocation: boolean) => HTMLElement;
    private createCluster: (total: number) => HTMLElement;
    private onLoading: (loading: boolean) => void;
    private onMarkerClick: (event: Event, coordinates: Position, listingId: string) => void;
    private onZoom: (zoomLevel: number) => void;
    private onLoad: () => void;
    private onClick: () => void;

    private markersOnScreen: Map<string, maplibregl.Marker> = new Map()
    private markers: Map<string, maplibregl.Marker> = new Map()

    constructor(
        mapConfig: MapConfig,
        createListing: (priceTag: string, id: string, exactLocation: boolean) => HTMLElement,
        createCluster: (total: number) => HTMLElement,
        onLoading: (loading: boolean) => void,
        onMarkerClick: (event: Event, coordinates: Position, listingId: string) => void,
        onZoom: (zoomLevel: number) => void,
        onLoad: () => void,
        onClick: () => void,
    ) {
        this.createListing = createListing;
        this.createCluster = createCluster;
        this.onLoading = onLoading;
        this.onMarkerClick = onMarkerClick;
        this.onZoom = onZoom;
        this.onLoad = onLoad;
        this.onClick = onClick;
        this.mapConfig = mapConfig;
    }

    public zoomOn(coordinates: Position) {
        this.map?.flyTo({
            center: [coordinates[0], coordinates[1]],
            zoom: this.map.getZoom() + 2
        })
    }

    private updateMarkers() {
        const features = this.map!!.querySourceFeatures('listings');

        const newViewMarkers: Map<string, maplibregl.Marker> = new Map(
            features
                .map((feature) => {
                    const props = feature.properties;
                    const id: string = props.cluster ? props.cluster_id : props.id;
                    if (this.markers.has(id)) {
                        return [id, this.markers.get(id) as maplibregl.Marker]
                    }

                    // @ts-ignore
                    const coordinates: Position = feature.geometry.coordinates;

                    let element = (!props.cluster) ? this.createListing(props.priceTag, props.id, props.exactLocation) : this.createCluster(props.point_count);
                    let newMarker = new maplibregl.Marker({element: element});
                    newMarker.setLngLat([coordinates[0], coordinates[1]]);

                    newMarker.getElement().addEventListener('click', (event) => this.onMarkerClick(event, coordinates, props.id));
                    this.markers.set(id, newMarker);
                    return [id, newMarker]
                })
        );

        newViewMarkers.forEach((marker: Marker, key: string, map: Map<string, Marker>) => {
            if (!this.markersOnScreen.has(key)) marker.addTo(this.map!!);
        });

        this.markersOnScreen.forEach((marker: Marker, key: string, map: Map<string, Marker>) => {
            if (!newViewMarkers.has(key)) marker.remove()
        });
        this.onLoading(false);
        this.markersOnScreen = newViewMarkers;
    }

    public init(mapEl: HTMLElement) {
        if (this.map) {
            this.map.resize();
            return;
        }

        this.onLoading(true);
        this.map = new maplibregl.Map({
            attributionControl: false,
            container: mapEl, // container id
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [this.mapConfig.longitude, this.mapConfig.latitude],
            zoom: this.mapConfig.zoom,
            // @ts-ignore
            transformRequest: this.transformRequest.bind(this),
            maxZoom: 16,
            minZoom: 8
        });

        this.map.addControl(new maplibregl.NavigationControl({
            showZoom: true,
            showCompass: false,
        }), 'bottom-right');

        this.map?.on('data', (e: MapSourceDataEvent) => {
            if (e.sourceId !== 'listings' || !e.isSourceLoaded) return;
            this.map?.on('move', this.updateMarkers.bind(this));
            this.map?.on('moveend', this.updateMarkers.bind(this));
            this.updateMarkers();
        });

        this.map?.on('zoom', (e: MapLibreZoomEvent) => this.onZoom(this.map?.getZoom() || 1))
        this.map?.on('load', this.onLoad);
        this.map?.on('click', (e: maplibregl.MapMouseEvent) => { this.onClick() })

    }

    loadPoints(points: GeoJSON.GeoJSON) {
        this.map!!.addSource('listings', {
            type: 'geojson',
            data: points,
            cluster: true,
            clusterMaxZoom: 14, // Max zoom to cluster points on
            clusterRadius: 50,
        })

        // @ts-ignore
        this.map?.fitBounds(boundingBoxFrom(points.features.map(point => point.geometry.coordinates)))

        this.map!!.addLayer({
            'id': 'points',
            'type': 'circle',
            'source': 'listings',
            'filter': ['!=', 'cluster', true],
            'paint': {
                'circle-opacity': 0,
            }
        });
    }

    createPopup(point: MapPoint, content: string) {
        let popup =  new maplibregl.Popup({
            offset: 20,
            closeButton: false,
            maxWidth: '328px',
            focusAfterOpen: false,
        })
            .setHTML(content)
            .setLngLat([point.longitude, point.latitude])
            .addTo(this.map!!);
        this.ensurePopupIsVisible(popup, point);
    }

    private ensurePopupIsVisible(popup: maplibregl.Popup, point: MapPoint) {
        setTimeout(() => popup.setLngLat([point.longitude, point.latitude]), 0)
    }

    private transformRequest(url: string, resourceType: string) {
        if (isMapboxURL(url)) {
            return transformMapboxUrl(url, resourceType, this.mapConfig.accessToken)
        }

        return {url}
    }
}